/// <amd-module name="Wit/WorkItemGitLinks/Components/Commit/CommitPickerTable" />
import * as React from "react";
import { useEffect, useMemo, useRef } from "react";
import { ObservableArray, ObservableValue, useSubscription } from "VSS/Core/Observable";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ListSelection } from "VSSUI/List";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { ColumnSelect, ITableColumn, renderSimpleCell, SimpleTableCell, Table } from "VSSUI/Table";
import { GitCommitRef } from "VC/Common/Contracts/Git";
import * as Resources from "Wit/WorkItemGitLinks/Resources";
import { authorToBasicIdentityRef } from "Wit/WorkItemGitLinks/GitLinkUtils";

export interface ICommitsPreviewTableProps {
    loading: boolean;
    commits: GitCommitRef[];
    onCommitSelected: (commitId: string) => void;
}

// The table typings require an index signature even though it is not actually needed.
type CommitListItem = GitCommitRef & Record<string, any>;

const numberOfPlaceholderRows = 5;
const placeholderItemProvider = new ObservableArray(
    new Array(numberOfPlaceholderRows).fill(new ObservableValue<CommitListItem | undefined>(undefined))
);

export function CommitPickerTable({ commits, onCommitSelected, loading }: ICommitsPreviewTableProps) {
    const columns = useMemo(() => getTableColumns(), []);
    const selection = useRef(new ListSelection({ selectOnFocus: false, multiSelect: false }));

    // Initialize the selection when the commits change
    useEffect(() => {
        if (commits.length > 0) {
            selection.current.select(0);
            onCommitSelected(commits[0].commitId);
        }
    }, [commits]);

    useSubscription(
        selection.current,
        () => {
            for (let i = 0; i < commits.length; i++) {
                if (selection.current.selected(i)) {
                    onCommitSelected(commits[i].commitId);
                    break;
                }
            }
        },
        [commits]
    );

    return (
        <Table
            itemProvider={loading ? placeholderItemProvider : new ArrayItemProvider(commits as CommitListItem[])}
            virtualize={false /* at most 50 rows*/}
            selection={selection.current}
            columns={columns}
        />
    );
}

function getTableColumns(): ITableColumn<CommitListItem>[] {
    return [
        new ColumnSelect(),
        {
            id: "commitId",
            name: Resources.CommitPickerTableIdColumn,
            readonly: true,
            renderCell: renderSimpleCell,
            width: -20
        },
        {
            id: "comment",
            name: Resources.CommitPickerTableMessageColumn,
            readonly: true,
            renderCell: renderSimpleCell,
            width: -40
        },
        {
            id: "author",
            name: Resources.CommitPickerTableAuthorColumn,
            readonly: true,
            renderCell: (iRow, iCol, col, item) => {
                return (
                    <SimpleTableCell className={col.className} key={iCol} tableColumn={col} columnIndex={iCol}>
                        <Persona className="icon-margin" identity={authorToBasicIdentityRef(item.author)} size={PersonaSize.size20} />
                        {item.author.name}
                    </SimpleTableCell>
                );
            },
            width: -20
        },
        {
            id: "authoredDate",
            name: Resources.CommitPickerTableAuthoredDateColumn,
            readonly: true,
            renderCell: (iRow, iCol, col, item) => {
                return (
                    <SimpleTableCell className={col.className} key={iCol} tableColumn={col} columnIndex={iCol}>
                        {item.author.date.toDateString()}
                    </SimpleTableCell>
                );
            },
            width: -20
        }
    ];
}
